import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { apiBaseUrl } from '../../../../util/api';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  CartIcon,
  ExternalLink,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import ForumMessagesLink from '../ForumMessagesLink/ForumMessagesLink';
import { TransactionsIcon } from '../MobileBottomNav/MobileBottomNavIcons';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const R2RLoginLink = ({ className, id = 'r2r-login-link', inMenu = false }) => {
  return (
    <a id={id} href={`${apiBaseUrl()}/api/auth/xenforo`} className={className || css.topbarLink}>
      {inMenu ? <span className={css.menuItemBorder} /> : null}
      {inMenu ? (
        <FormattedMessage id="TopbarDesktop.loginWithR2R" />
      ) : (
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.loginWithR2R" />
        </span>
      )}
    </a>
  );
};

export const formatInboxBadge = count => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n > 99 ? '99+' : String(Math.floor(n));
};

const inboxAriaLabel = (intl, count) => {
  const n = Number(count);
  return Number.isFinite(n) && n > 0
    ? intl.formatMessage({ id: 'TopbarDesktop.yourTransactionsUnread' }, { count: n })
    : intl.formatMessage({ id: 'TopbarDesktop.yourTransactions' });
};

const CustomMenuLink = ({ linkConfig, currentPage }) => {
  const { text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab =>
      (hasPageName('InboxPage') || hasPageName('TransactionsPage')) &&
      currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <NamedLink
        name={name}
        params={params}
        to={to}
        className={classNames(css.menuLink, getCurrentPageClass(name))}
      >
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.menuLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const DirectoryMenuLink = ({ currentPageClass }) => (
  <NamedLink
    className={classNames(css.menuLink, currentPageClass('SellerDirectoryPage'))}
    name="SellerDirectoryPage"
  >
    <span className={css.menuItemBorder} />
    <FormattedMessage id="TopbarDesktop.directory" />
  </NamedLink>
);

const customLinkMenuItems = (customLinks, currentPage) =>
  (customLinks || []).map((linkConfig, index) => (
    <MenuItem key={`${linkConfig.text}_${index}`}>
      <CustomMenuLink linkConfig={linkConfig} currentPage={currentPage} />
    </MenuItem>
  ));

const SignedInMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  inboxTab,
  intl,
  customLinks,
  notificationCount,
}) => {
  const userUUID = currentUser?.id?.uuid;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isTransactionsPage =
      page === 'TransactionsPage' && currentPage?.indexOf('TransactionsPage') === 0;
    return currentPage === page || isAccountSettingsPage || isTransactionsPage
      ? css.currentPage
      : null;
  };
  const badge = formatInboxBadge(notificationCount);
  const txLabel = inboxAriaLabel(intl, notificationCount);

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="signed-in-menu-label"
        className={css.guestMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.menu" />
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="marketplaceGroup">
          <h2 className={css.groupLabel}>
            <FormattedMessage id="TopbarDesktop.marketplaceGroup" />
          </h2>
        </MenuItem>
        <MenuItem key="SellerDirectoryPage">
          <DirectoryMenuLink currentPageClass={currentPageClass} />
        </MenuItem>
        {customLinkMenuItems(customLinks, currentPage)}
        <MenuItem key="accountGroup">
          <h2 className={css.groupLabel}>
            <FormattedMessage id="TopbarDesktop.accountGroup" />
          </h2>
        </MenuItem>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="WatchedListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('WatchedListingsPage'))}
            name="WatchedListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.watchedListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="TransactionsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('TransactionsPage'))}
            name="TransactionsPage"
            params={{ tab: inboxTab }}
            title={txLabel}
            ariaLabel={txLabel}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourTransactionsLink" />
            {badge ? (
              <span className={css.menuInboxBadge} aria-hidden="true">
                {badge}
              </span>
            ) : null}
          </NamedLink>
        </MenuItem>
        {userUUID ? (
          <MenuItem key="ProfilePage">
            <NamedLink className={css.menuLink} name="ProfilePage" params={{ id: userUUID }}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.viewProfileLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const GuestMenu = ({ currentPage, customLinks }) => {
  const currentPageClass = page => (currentPage === page ? css.currentPage : null);

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="guest-menu-label"
        className={css.guestMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.menu" />
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="SellerDirectoryPage">
          <DirectoryMenuLink currentPageClass={currentPageClass} />
        </MenuItem>
        {customLinkMenuItems(customLinks, currentPage)}
        <MenuItem key="login">
          <R2RLoginLink id="r2r-login-link-menu" className={css.menuLink} inMenu />
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {number} [props.forumUnreadCount]
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    forumUnreadCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const txLabel = inboxAriaLabel(intl, notificationCount);
  const txBadge = formatInboxBadge(notificationCount);
  const onTransactionsPage = currentPage?.indexOf('TransactionsPage') === 0;
  const transactionsLinkMaybe = authenticatedOnClientSide ? (
    <NamedLink
      className={classNames(css.transactionsLink, {
        [css.transactionsLinkCurrent]: onTransactionsPage,
      })}
      name="TransactionsPage"
      params={{ tab: inboxTab }}
      title={txLabel}
      ariaLabel={txLabel}
      aria-current={onTransactionsPage ? 'page' : undefined}
    >
      <span className={css.transactionsIconWrap}>
        <TransactionsIcon size={22} />
        {txBadge ? (
          <span className={css.transactionsBadge} aria-hidden="true">
            {txBadge}
          </span>
        ) : null}
      </span>
    </NamedLink>
  ) : null;

  const messagesLinkMaybe = authenticatedOnClientSide ? (
    <ForumMessagesLink
      className={css.messagesLink}
      iconWrapClassName={css.messagesIconWrap}
      badgeClassName={css.messagesBadge}
      unreadCount={forumUnreadCount}
      iconSize={22}
    />
  ) : null;

  const signedInMenuMaybe = authenticatedOnClientSide ? (
    <SignedInMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      inboxTab={inboxTab}
      intl={intl}
      customLinks={customLinks}
      notificationCount={notificationCount}
    />
  ) : null;

  const profileAvatarMaybe =
    authenticatedOnClientSide && currentUser?.id?.uuid ? (
      <NamedLink
        className={css.profileLink}
        name="ProfilePage"
        params={{ id: currentUser.id.uuid }}
        title={intl.formatMessage({ id: 'TopbarDesktop.viewProfileLink' })}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.viewProfileLink' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </NamedLink>
    ) : null;

  const guestMenuMaybe = isAuthenticatedOrJustHydrated ? null : (
    <GuestMenu currentPage={currentPage} customLinks={customLinks} />
  );

  const r2rLoginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <R2RLoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, css.takeAvailableSpace)}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, css.takeAvailableSpace)}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        id="logo-topbar-desktop"
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
        showBrandName
      />
      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={[]}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      />

      {transactionsLinkMaybe}
      {messagesLinkMaybe}

      {/* Signed in or out: a guest cart is a real cart. Right of Messages when signed in. */}
      <CartIcon
        className={css.cartLink}
        iconWrapClassName={css.cartIconWrap}
        badgeClassName={css.cartBadge}
        iconSize={22}
      />

      {signedInMenuMaybe}
      {profileAvatarMaybe}
      {guestMenuMaybe}
      {r2rLoginLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
